import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button/Button';
import { Dialog } from '../Dialog/Dialog';
import { Field } from '../Form/Field/Field';
import { Form } from '../Form/Form/Form';
import { Input } from '../Form/Input/Input';
import { formStyle, inputStyle } from './InsertLinkDialog.css';

type Props = {
  /** Href of the link the caret sits in, if any. Turns the dialog into an edit form for that link. */
  link?: string;
  onInsert: (values: { link: string; text: string }) => void;
  withTextField: boolean;
};

export const InsertLinkDialog = (props: Props) => {
  return (
    <Dialog.Body>
      <InsertLinkForm {...props} />
    </Dialog.Body>
  );
};

const InsertLinkForm = ({ link = '', onInsert, withTextField }: Props) => {
  const { control, handleSubmit } = useForm({ defaultValues: { link, text: '' } });

  const { t } = useTranslation(['common']);

  const isEditing = link !== '';

  const onSubmit = handleSubmit(({ link, text }) => {
    if (link) onInsert({ link, text });
  });

  return (
    <>
      <Dialog.Header title={t(isEditing ? 'common:richText.linkModal.editTitle' : 'common:richText.linkModal.title')} />

      <Form
        className={formStyle}
        onSubmit={(event) => {
          event.stopPropagation();
          onSubmit(event);
        }}
      >
        {/* Without a selection there is no text to turn into a link, so the text to insert is asked for instead. */}
        {withTextField && (
          <Controller
            control={control}
            name="text"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label>{t('common:richText.linkModal.textLabel')}</Field.Label>
                <Input autoFocus className={inputStyle} onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />
              </Field.Root>
            )}
          />
        )}

        <Controller
          control={control}
          name="link"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('common:richText.linkModal.label')}</Field.Label>
              <Input autoFocus={!withTextField} className={inputStyle} onBlur={onBlur} onValueChange={onChange} ref={ref} value={value} />
            </Field.Root>
          )}
        />

        <Dialog.Actions>
          <Dialog.Close render={<Button type="submit">{t(isEditing ? 'common:save' : 'common:add')}</Button>} />
        </Dialog.Actions>
      </Form>
    </>
  );
};
